import React, { useMemo } from "react";
import {
  Alert,
  Button,
  Empty,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import { useListResourceSessionsQuery } from "../../../store/api/resourceSessionsApi";
import { useGetResourcesQuery } from "../../../store/api/resourcesApi";
import {
  EducationLevel,
  ResourceSessionRow,
  ResourceSessionType,
} from "../../../services/resourceSessions/resourceSessions.types";
import {
  describeEvaluation,
  describeStars,
  MAX_STARS,
  starTagColor,
} from "./starMeta";
import {
  ClassroomFilterSelect,
  fmtDateTime,
  SessionDateRange,
  StudentCell,
  StudentSearchInput,
} from "./SessionFilterControls";
import {
  parsePositiveInt,
  useClampSessionPage,
  useSessionFilters,
} from "./useSessionFilters";

const { Text } = Typography;

/** Params que solo tienen sentido en esta pestaña. */
export const RESOURCE_TAB_PARAMS = ["resourceId"] as const;

const TYPE_COPY: Record<
  ResourceSessionType,
  { column: string; filter: string; empty: string }
> = {
  GAME: {
    column: "Juego",
    filter: "Filtrar por juego",
    empty: "No hay partidas para los filtros elegidos.",
  },
  VIDEO: {
    column: "Video",
    filter: "Filtrar por video",
    empty: "No hay videos vistos para los filtros elegidos.",
  },
  READING: {
    column: "Lectura",
    filter: "Filtrar por lectura",
    empty: "No hay lecturas para los filtros elegidos.",
  },
};

/** Qué es cada fila, dicho para quien opera el back office. */
const explain = (type: ResourceSessionType, level: EducationLevel): string => {
  if (type === "GAME" && level === "PRIMARIA") {
    return 'Cada partida calificada es una fila, también cuando el alumno repite el juego, y aparece sólo si tocó "Continuar" en la tarjeta de resultados: salir antes, o quedarse en "Volver a jugar", no reporta nada. Los juegos que no reportan calificación aparecen una sola vez: la primera vez que el alumno los terminó.';
  }
  if (type === "GAME") {
    return "Los juegos de Kinder no reportan calificación: cada fila es la primera vez que el alumno terminó el juego. Volver a jugarlo no agrega una fila nueva.";
  }
  const noun = type === "VIDEO" ? "el video" : "la lectura";
  const again = type === "VIDEO" ? "Volver a verlo" : "Volver a leerla";
  return `Cada fila es la primera vez que el alumno terminó ${noun}. ${again} no agrega una fila nueva.`;
};

/**
 * Juegos, videos y lecturas terminados (SCRUM-1167). Los juegos de Primaria
 * suman además cada partida calificada (SCRUM-645), con su nota y estrellas.
 */
const ResourceSessionsTab: React.FC<{
  type: ResourceSessionType;
  level: EducationLevel;
}> = ({ type, level }) => {
  const {
    searchParams,
    page,
    pageSize,
    userSearch,
    classroomId,
    from,
    to,
    hasSharedFilters,
    setFilterParams,
    setPage,
  } = useSessionFilters();
  const resourceId = parsePositiveInt(searchParams.get("resourceId"));
  const copy = TYPE_COPY[type];
  // Kinder no tiene partidas calificadas: sus columnas estarían siempre vacías.
  const showRounds = type === "GAME" && level === "PRIMARIA";

  const { data: resources } = useGetResourcesQuery();

  const resourceOptions = useMemo(
    () =>
      (resources ?? [])
        .filter((resource) => resource.type === type)
        .map((resource) => ({ value: resource.id, label: resource.title })),
    [resources, type],
  );

  const { data, isFetching, isError, refetch } = useListResourceSessionsQuery({
    type,
    level,
    page,
    pageSize,
    ...(userSearch && { userSearch }),
    ...(classroomId !== undefined && { classroomId }),
    ...(resourceId !== undefined && { resourceId }),
    ...(from && { from }),
    ...(to && { to }),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  useClampSessionPage(total, page, pageSize, setPage);

  const hasActiveFilters = hasSharedFilters || resourceId !== undefined;

  const handleClearFilters = () =>
    setFilterParams({
      userSearch: undefined,
      classroomId: undefined,
      resourceId: undefined,
      from: undefined,
      to: undefined,
    });

  const roundColumns: ColumnsType<ResourceSessionRow> = [
    {
      title: "Calificación",
      key: "evaluation",
      width: 150,
      render: (_value, row) =>
        row.evaluation === null ? (
          <Tooltip title="Este juego no reporta calificación: la fila es la primera vez que el alumno lo terminó.">
            <Text type="secondary">Sin calificación</Text>
          </Tooltip>
        ) : (
          <Tooltip title={describeEvaluation(row.evaluation)}>
            <Text strong>{row.evaluation} / 10</Text>
          </Tooltip>
        ),
    },
    {
      title: "Estrellas",
      key: "stars",
      width: 110,
      render: (_value, row) =>
        row.stars === null ? (
          <Text type="secondary">—</Text>
        ) : (
          <Tooltip title={describeStars(row.stars, row.evaluation)}>
            <Tag color={starTagColor(row.stars)}>
              {row.stars} / {MAX_STARS}
            </Tag>
          </Tooltip>
        ),
    },
    {
      title: "Tarea",
      key: "homework",
      width: 160,
      render: (_value, row) => {
        if (row.source === "COMPLETION") {
          return (
            <Tooltip title="El primer terminado no registra desde qué tarea se jugó.">
              <Text type="secondary">—</Text>
            </Tooltip>
          );
        }
        return row.homework ? (
          row.homework.title
        ) : (
          <Text type="secondary">Fuera de una tarea</Text>
        );
      },
    },
    {
      title: "gameId",
      dataIndex: "game_id",
      key: "game_id",
      width: 160,
      render: (value: string | null) =>
        value ? <Text code>{value}</Text> : <Text type="secondary">—</Text>,
    },
  ];

  const columns: ColumnsType<ResourceSessionRow> = [
    {
      title: "Alumno",
      key: "user",
      render: (_value, row) => <StudentCell user={row.user} />,
    },
    {
      title: "Grupo",
      key: "classrooms",
      width: 200,
      render: (_value, row) =>
        row.classrooms.length > 0 ? (
          row.classrooms.join(", ")
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: copy.column,
      key: "resource",
      render: (_value, row) => row.resource.title,
    },
    ...(showRounds ? roundColumns : []),
    {
      title: "Fecha",
      dataIndex: "created_at",
      key: "created_at",
      width: 170,
      render: (iso: string) => fmtDateTime(iso),
    },
  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Space wrap>
        <StudentSearchInput
          value={userSearch}
          onCommit={(value) => setFilterParams({ userSearch: value })}
        />

        <ClassroomFilterSelect
          value={classroomId}
          onChange={(value) =>
            setFilterParams({
              classroomId: value !== undefined ? String(value) : undefined,
            })
          }
        />

        <Select<number | undefined>
          allowClear
          showSearch
          placeholder={copy.filter}
          style={{ width: 260 }}
          value={resourceId}
          onChange={(value) =>
            setFilterParams({
              resourceId: value !== undefined ? String(value) : undefined,
            })
          }
          options={resourceOptions}
          filterOption={(input, option) =>
            String(option?.label ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />

        <SessionDateRange from={from} to={to} onChange={setFilterParams} />

        {hasActiveFilters && (
          <Button onClick={handleClearFilters}>Limpiar filtros</Button>
        )}
      </Space>

      <Alert type="info" showIcon message={explain(type, level)} />

      {isError && (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar las sesiones."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      )}

      <Table<ResourceSessionRow>
        size="small"
        columns={columns}
        dataSource={items}
        rowKey="key"
        loading={isFetching}
        locale={{
          emptyText: (
            <Empty description={copy.empty}>
              {hasActiveFilters && (
                <Button onClick={handleClearFilters}>Limpiar filtros</Button>
              )}
            </Empty>
          ),
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onChange: setPage,
        }}
      />
    </Space>
  );
};

export default ResourceSessionsTab;
